import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Check } from "lucide-react";

/**
 * Compact self-playing scene for the synapse bento cover: a finished notation note
 * funnels field-by-field through the synapse extension into "AnyEMR". Funnel lines
 * flow from the note's corners, converge at synapse, then fan into AnyEMR.
 */

const FIELDS = ["Subjective", "Objective", "Assessment", "Plan"];
const TOTAL = FIELDS.length;
const STEP_MS = 700;
const LOOP_GAP_MS = 3000;

export default function SynapseCoverWidget() {
  const [written, setWritten] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setWritten(TOTAL);
      return;
    }
    const isLast = written >= TOTAL;
    timeoutRef.current = window.setTimeout(
      () => setWritten((w) => (w >= TOTAL ? 0 : w + 1)),
      isLast ? LOOP_GAP_MS : STEP_MS
    );
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [written]);

  const complete = written >= TOTAL;
  const flowing = !complete;

  return (
    <div className="absolute inset-0 p-4 pointer-events-none">
      <div className="relative w-full h-full max-w-[320px] mx-auto">
        {/* Funnel lines: note corners → synapse → AnyEMR corners */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {[
            "M34 30 L50 50 L66 30",
            "M34 70 L50 50 L66 70",
          ].map((d, i) => (
            <motion.path
              key={i}
              d={d}
              fill="none"
              stroke="#486633"
              strokeWidth={1}
              strokeLinecap="round"
              strokeDasharray="3 4"
              vectorEffect="non-scaling-stroke"
              opacity={0.55}
              animate={flowing ? { strokeDashoffset: [14, 0] } : { strokeDashoffset: 0 }}
              transition={
                flowing
                  ? { duration: 0.9, repeat: Infinity, ease: "linear" }
                  : { duration: 0.3 }
              }
            />
          ))}
        </svg>

        {/* Source note */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[34%] rounded-xl bg-white/95 shadow-[0_10px_30px_-12px_rgba(31,61,59,0.4)] px-2.5 py-2 z-10">
          <div className="flex items-center gap-1 mb-1.5">
            <FileText className="w-3 h-3 text-[#495B25]" />
            <span
              className="text-[10px] leading-none"
              style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", color: "#429CCB" }}
            >
              notation
            </span>
          </div>
          {FIELDS.map((f, i) => (
            <div
              key={f}
              className={`h-1.5 rounded-full mb-1 ${
                i < written ? "bg-[#486633]/60" : "bg-gray-200"
              }`}
              style={{ width: `${70 + (i % 2) * 20}%` }}
            />
          ))}
        </div>

        {/* synapse hub */}
        <span
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 rounded-full bg-[#1F3D3B] px-2 py-0.5 text-[8px] font-semibold text-white shadow-md"
          style={{ fontFamily: "'Baloo Bhaina 2', sans-serif" }}
        >
          synapse
        </span>

        {/* AnyEMR target */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[34%] rounded-xl bg-white/95 shadow-[0_10px_30px_-12px_rgba(31,61,59,0.4)] px-2.5 py-2 z-10">
          <div className="flex items-center gap-1 mb-1.5">
            <span className="w-2.5 h-2.5 rounded-[3px] bg-[#334155]" />
            <span className="text-[9px] font-bold text-[#334155]">AnyEMR</span>
          </div>
          {FIELDS.map((f, i) => (
            <div
              key={f}
              className={`h-1.5 rounded-full mb-1 transition-colors duration-300 ${
                i < written ? "bg-[#486633]/60" : "bg-gray-100"
              }`}
              style={{ width: `${70 + (i % 2) * 20}%` }}
            />
          ))}
        </div>

        {/* Complete badge */}
        <AnimatePresence>
          {complete && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 whitespace-nowrap flex items-center gap-1 rounded-full bg-white px-2.5 py-1 shadow-md z-30"
            >
              <span className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#486633]">
                <Check className="w-2 h-2 text-white" />
              </span>
              <span className="text-[9px] font-semibold text-[#1F3D3B]">
                Transfer complete
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
